import React from 'react';
import { X, Calendar } from 'lucide-react';
import { DeleteRequestStatus, DeleteRequestType } from '../types';

interface AdminDeleteRequestFiltersProps {
  statusFilter: DeleteRequestStatus | 'all';
  typeFilter: DeleteRequestType | 'all';
  dateFrom: string;
  dateTo: string;
  searchQuery: string;
  onStatusChange: (status: DeleteRequestStatus | 'all') => void;
  onTypeChange: (type: DeleteRequestType | 'all') => void;
  onDateFromChange: (date: string) => void;
  onDateToChange: (date: string) => void;
  onSearchChange: (query: string) => void;
  onClearFilters: () => void;
  stats?: {
    totalRequests: number;
    pendingRequests: number;
    approvedThisMonth: number;
    rejectedThisMonth: number;
  };
}

export const AdminDeleteRequestFilters: React.FC<AdminDeleteRequestFiltersProps> = ({
  statusFilter,
  typeFilter,
  dateFrom,
  dateTo,
  searchQuery,
  onStatusChange,
  onTypeChange,
  onDateFromChange,
  onDateToChange,
  onSearchChange,
  onClearFilters,
  stats,
}) => {
  const hasActiveFilters =
    statusFilter !== 'all' ||
    typeFilter !== 'all' ||
    dateFrom ||
    dateTo ||
    searchQuery;

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={onClearFilters}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <X className="h-4 w-4" />
            Clear All
          </button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6 pb-6 border-b">
          <div className="bg-slate-50 p-4 rounded-lg">
            <p className="text-sm text-slate-600 mb-1">Total Requests</p>
            <p className="text-2xl font-bold text-slate-900">{stats.totalRequests}</p>
          </div>
          <div className="bg-amber-50 p-4 rounded-lg">
            <p className="text-sm text-amber-700 mb-1">Pending</p>
            <p className="text-2xl font-bold text-amber-900">{stats.pendingRequests}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <p className="text-sm text-green-700 mb-1">Approved This Month</p>
            <p className="text-2xl font-bold text-green-900">{stats.approvedThisMonth}</p>
          </div>
          <div className="bg-red-50 p-4 rounded-lg">
            <p className="text-sm text-red-700 mb-1">Rejected This Month</p>
            <p className="text-2xl font-bold text-red-900">{stats.rejectedThisMonth}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Search
          </label>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Name or email..."
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Status
          </label>
          <select
            value={statusFilter}
            onChange={(e) => onStatusChange(e.target.value as DeleteRequestStatus | 'all')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            Account Type
          </label>
          <select
            value={typeFilter}
            onChange={(e) => onTypeChange(e.target.value as DeleteRequestType | 'all')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          >
            <option value="all">All Types</option>
            <option value="user">Service Users</option>
            <option value="provider">Providers</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            From Date
          </label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => onDateFromChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            <Calendar className="h-4 w-4 inline mr-1" />
            To Date
          </label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => onDateToChange(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent"
          />
        </div>
      </div>

      {hasActiveFilters && (
        <div className="mt-4 flex flex-wrap gap-2">
          {searchQuery && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
              Search: {searchQuery}
              <button onClick={() => onSearchChange('')} className="hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {statusFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
              Status: {statusFilter}
              <button onClick={() => onStatusChange('all')} className="hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {typeFilter !== 'all' && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
              Type: {typeFilter}
              <button onClick={() => onTypeChange('all')} className="hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {dateFrom && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
              From: {dateFrom}
              <button onClick={() => onDateFromChange('')} className="hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
          {dateTo && (
            <span className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
              To: {dateTo}
              <button onClick={() => onDateToChange('')} className="hover:text-slate-900">
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      )}
    </div>
  );
};
